import React from "react";
import RadioGroupField from "./FormFields/RadioGroupFied";
import DropDownField from "./FormFields/DropDownField";
import UploadFileField from "./FormFields/UploadFileField";
import TextAreaField from "./FormFields/TextAreaField";
import LabelField from "./FormFields/LabelField";
import { FieldError } from "react-hook-form";
import TextInputField from "./FormFields/TextInputField";

// Define the options type for dropdown and radio fields
interface Option {
  id: string; // or number, depending on your data structure
  name: string;
}

// Define the Field type
interface Field {
  type: string;
  name: string;
  label: string;
  options?: Option[];
  error: string;
  readOnly?: boolean;
  onBlur?: () => void;
  selectedValue?: string;
  onChange?: (value: string) => void;
  onMenuClose?: (value: string) => void;
  onMenuOpen?: (value: string) => void;
  uploadApi?: string;
  condition?: () => boolean;
  placeholder?: string;
  className?: string;
  autoGenerate?: boolean;
  printingProgress?: boolean;
  defaultValue?: string;
  multiple?: boolean;
  category?: string;
  isMulti?: boolean;
  apiUrl?: string;
  isDisabled?: boolean;
  setSelectedCode: (value: string) => void;
}

// Define the props for the FieldRenderer component
interface FieldRendererProps {
  field: Field;
  register: any;
  errors: Record<string, any>;
  setValue: (name: string, value: any) => void;
  watch: (name: string) => any;
  setError: (name: string, error: FieldError) => void;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  register,
  errors,
  setValue,
  watch,
  setError,
}) => {
  const {
    type,
    name,
    label,
    options,
    error,
    readOnly,
    onBlur,
    onChange,
    onMenuClose,
    onMenuOpen,
    uploadApi,
    condition,
    className,
    defaultValue,
    multiple,
    category,
    isMulti,
    apiUrl,
    setSelectedCode,
    defaultCode,
    isDisabled
  } = field;

  if (condition && !condition()) return null; // Skip rendering if the condition is not met

  // Watch the current value of the field to ensure controlled inputs
  var fieldValue;
  if (watch) {
    fieldValue = watch(name);
  }

  const handleFieldChange = (value: any, evetn) => {
    setValue(name, value, { shouldValidate: true });
    onChange?.(value); // Call external onChange if provided
  };

  switch (category) {
    case "input":
      return (
        <div className="col-span-1 mb-5">
          {label && <LabelField label={label} id={label} />}
          <TextInputField
            name={name}
            register={register}
            error={errors[name]}
            placeholder={field.placeholder}
            readOnly={readOnly}
            onBlur={onBlur}
            type={type}
            className="w-full mt-1 p-2 border-[1px] border-[#E2E8F0] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder:text-[#94A3B8E5] placeholder:text-[14px]"
            // value={fieldValue}
            // onChange={(e:any  , event :any) => handleFieldChange(e,)} // Handle input change
          />
        </div>
      );

    case "date":
      return (
        <div className="col-span-1  mb-5">
          <LabelField label={label} id={undefined} />
          <TextInputField
            name={name}
            register={register}
            error={errors[name]}
            placeholder={field.placeholder}
            readOnly={readOnly}
            onBlur={onBlur}
            type={type}
            value={fieldValue} // Controlled input for date
            onChange={(e: any) => handleFieldChange(e.target.value)} // Handle date change
          />
        </div>
      );

    case "dropDown":
      return (
        <div className="col-span-1  ">
          {label && <LabelField label={label} id={undefined} />}
          <DropDownField
            name={name}
            options={options}
            register={
              register ??
              (() => ({ onChange: () => {}, onBlur: () => {}, ref: null }))
            }
            multiple={multiple}
            error={errors?.[name]}
            setValue={setValue}
            // onChange={(e) => handleFieldChange(e.value)} // Use setValue for dropdown
            onChange={onChange}
            onMenuClose={onMenuClose}
            placeholder={field.placeholder}
            value={fieldValue} // Ensure controlled input for dropdown
            defaultValue={defaultValue || ""} // Ensure default value is passed
            apiUrl={apiUrl}
            setSelectedCode={setSelectedCode}
            onMenuOpen={onMenuOpen}
            defaultCode={defaultCode}
            isDisabled={isDisabled}
            // className="w-full mt-1 p-2 border-[1px] border-[#E2E8F0] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 placeholder:text-[#94A3B8E5] placeholder:text-[14px]"
          />
        </div>
      );

    case "textArea":
      return (
        <div className={className ? className : ""}>
          {label && <LabelField label={label} id={undefined} />}
          <TextAreaField
            name={name}
            register={register}
            error={errors[name]}
            setValue={setValue}
            placeholder={field.placeholder}
            value={fieldValue} // Controlled input for text area
            onChange={(e: any) => handleFieldChange(e.target.value)} // Handle text area change
          />
        </div>
      );

    case "file":
      return (
        <div className="col-span-1  mb-4">
          {label && <LabelField label={label} id={undefined} />}
          <UploadFileField
            name={name}
            uploadApi={uploadApi}
            register={register}
            error={errors?.[name]}
            setValue={setValue}
            readOnly={readOnly}
            watch={watch}
            type={type}
            setError={setError}
            multiple={multiple}
          />
        </div>
      );

    case "radio":
      return (
        <RadioGroupField
          name={name}
          options={options || []}
          register={register}
          error={errors[name]}
          setValue={setValue}
          onChange={onChange}
          readOnly={readOnly}
          type={type}
          value={fieldValue} // Ensure controlled input for radio
        />
      );

    default:
      return null;
  }
};

export default FieldRenderer;
